import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, API, getActiveBackendUrl } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, DollarSign, TrendingUp, TrendingDown, 
  FileText, Upload, X, ChevronLeft, ChevronRight,
  Eye, Download, FolderArchive, Car, Banknote, Link, Search, Filter, CreditCard, ArrowLeftRight, Clock, Check, PiggyBank
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import CategorySelect from '@/components/ui/CategorySelect';


export default function CompanyFinancials() {
  const { token } = useAuth();
  
  // Get backend URL lazily to avoid initialization order issues
  const BACKEND_URL = useMemo(() => getActiveBackendUrl(), []);
  
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState({ 
    income_categories: [], 
    expense_categories: [],
    cash_income_categories: [],
    cash_outcome_categories: []
  });
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState({ 
    previous_balance: 0,
    total_income: 0, 
    total_income_taxable_gross: 0,
    total_income_tax: 0,
    total_income_net: 0,
    total_expenses: 0, 
    total_cash_income: 0,
    total_cash_outcome: 0,
    cash_net: 0,
    month_net: 0,
    total_balance: 0,
    tax_rate: 0.24
  });
  const [taxRate, setTaxRate] = useState(0.24);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Advanced filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Active tab
  const [activeTab, setActiveTab] = useState('card');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryType, setEntryType] = useState('income');
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  
  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEntry, setPreviewEntry] = useState(null);
  
  // All files dialog state
  const [allFilesOpen, setAllFilesOpen] = useState(false);
  const [allFiles, setAllFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  
  // Transfer dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({
    direction: 'cash_to_card',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    notes: ''
  });
  const [submittingTransfer, setSubmittingTransfer] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    category: '',
    amount: '',
    vehicle_id: '',
    notes: '',
    file_data: null,
    file_name: '',
    payment_status: 'completed',
    taxable: false
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchCategories();
    fetchVehicles();
  }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/company-financials?year=${selectedYear}&month=${selectedMonth}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/company-financials/summary?year=${selectedYear}&month=${selectedMonth}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      if (typeof summaryRes.data?.tax_rate === 'number') setTaxRate(summaryRes.data.tax_rate);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/company-financials/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchAllFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await axios.get(`${API}/admin/all-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllFiles(response.data);
    } catch (error) {
      console.error('Error fetching all files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount || !formData.date || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        entry_type: entryType,
        date: formData.date,
        description: formData.description,
        category: formData.category,
        amount: parseFloat(formData.amount),
        vehicle_id: formData.vehicle_id || null,
        notes: formData.notes || null,
        file_data: formData.file_data,
        file_name: formData.file_name || null,
        payment_status: formData.payment_status || 'completed',
        // taxable only meaningful for POS income (entry_type='income')
        taxable: entryType === 'income' ? !!formData.taxable : false
      };

      if (editingEntry) {
        await axios.put(`${API}/company-financials/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Entry updated successfully');
      } else {
        await axios.post(`${API}/company-financials`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Entry created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
      fetchCategories();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry');
    }
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;

    try {
      await axios.delete(`${API}/company-financials/${entryToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted successfully');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const openCreateDialog = (type) => {
    setEntryType(type);
    setEditingEntry(null);
    resetForm();
    setDialogOpen(true);
  };

  const togglePaymentStatus = async (entry) => {
    const newStatus = entry.payment_status === 'pending' ? 'completed' : 'pending';
    try {
      await axios.put(`${API}/company-financials/${entry.id}`, 
        { payment_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (entry) => {
    setEntryType(entry.entry_type);
    setEditingEntry(entry);
    
    let dateValue = '';
    if (entry.date) {
      const date = new Date(entry.date);
      dateValue = date.toISOString().split('T')[0];
    }
    
    setFormData({
      date: dateValue,
      description: entry.description || '',
      category: entry.category || '',
      amount: entry.amount?.toString() || '',
      vehicle_id: entry.vehicle_id || '',
      notes: entry.notes || '',
      file_data: entry.file_data || null,
      file_name: entry.file_name || '',
      payment_status: entry.payment_status || 'completed',
      taxable: !!entry.taxable
    });
    setDialogOpen(true);
  };

  const openPreviewDialog = (entry) => {
    setPreviewEntry(entry);
    setPreviewOpen(true);
  };

  const openAllFilesDialog = () => {
    fetchAllFiles();
    setAllFilesOpen(true);
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      category: '',
      amount: '',
      vehicle_id: '',
      notes: '',
      file_data: null,
      file_name: '',
      payment_status: 'completed',
      taxable: false
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          file_data: reader.result,
          file_name: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setFormData(prev => ({
      ...prev,
      file_data: null,
      file_name: ''
    }));
  };

  const handleTransfer = async () => {
    if (!transferData.amount || parseFloat(transferData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!transferData.date) {
      toast.error('Please select a date');
      return;
    }
    setSubmittingTransfer(true);
    try {
      await axios.post(`${API}/company-financials/transfer`, {
        direction: transferData.direction,
        amount: parseFloat(transferData.amount),
        date: transferData.date,
        description: transferData.description || null,
        notes: transferData.notes || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transfer completed successfully');
      setTransferOpen(false);
      setTransferData({ direction: 'cash_to_card', amount: '', date: new Date().toISOString().split('T')[0], description: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmittingTransfer(false);
    }
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const downloadFile = (fileData, fileName) => {
    if (!fileData) return;
    
    const link = document.createElement('a');
    link.href = fileData;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  const downloadFileFromUrl = async (url, fileName) => {
    try {
      const response = await axios.get(`${BACKEND_URL}${url}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
  };

  const isPdfFile = (fileName) => {
    if (!fileName) return false;
    return fileName.toLowerCase().endsWith('.pdf');
  };

  // Search filter function
  const filterBySearch = (entry) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (entry.description && entry.description.toLowerCase().includes(query)) ||
      (entry.category && entry.category.toLowerCase().includes(query)) ||
      (entry.vehicle_plate && entry.vehicle_plate.toLowerCase().includes(query)) ||
      (entry.amount && entry.amount.toString().includes(query))
    );
  };

  // Advanced filter function
  const filterByAdvanced = (entry) => {
    // Category filter
    if (filterCategory !== 'all' && entry.category !== filterCategory) {
      return false;
    }
    
    // Date range filter
    if (filterDateFrom || filterDateTo) {
      const entryDate = entry.date ? new Date(entry.date) : null;
      if (entryDate) {
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (entryDate < fromDate) return false;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          if (entryDate > toDate) return false;
        }
      }
    }
    
    // Amount range filter
    if (filterAmountMin || filterAmountMax) {
      const amount = entry.amount || 0;
      if (filterAmountMin && amount < parseFloat(filterAmountMin)) return false;
      if (filterAmountMax && amount > parseFloat(filterAmountMax)) return false;
    }
    
    // Payment status filter
    if (filterStatus !== 'all') {
      const entryStatus = entry.payment_status || 'completed';
      if (entryStatus !== filterStatus) return false;
    }
    
    return true;
  };

  // Get all unique categories from entries
  const allCategories = [...new Set(entries.map(e => e.category).filter(Boolean))].sort();

  // Filter entries by type, search, and advanced filters
  const incomeEntries = entries.filter(e => e.entry_type === 'income' && filterBySearch(e) && filterByAdvanced(e));
  const expenseEntries = entries.filter(e => e.entry_type === 'expense' && filterBySearch(e) && filterByAdvanced(e));
  const cashIncomeEntries = entries.filter(e => e.entry_type === 'cash_income' && filterBySearch(e) && filterByAdvanced(e));
  const cashOutcomeEntries = entries.filter(e => e.entry_type === 'cash_outcome' && filterBySearch(e) && filterByAdvanced(e));
  const savingsEntries = entries.filter(e => e.entry_type === 'savings' && filterBySearch(e) && filterByAdvanced(e));

  // Check if any advanced filters are active
  const hasActiveFilters = filterCategory !== 'all' || filterDateFrom || filterDateTo || filterAmountMin || filterAmountMax || filterStatus !== 'all';

  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setFilterCategory('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterAmountMin('');
    setFilterAmountMax('');
    setFilterStatus('all');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'company_financial': return <DollarSign className="h-4 w-4" />;
      case 'expense_receipt': return <FileText className="h-4 w-4" />;
      case 'petrol_receipt': return <Car className="h-4 w-4" />;
      case 'ride_voucher': return <FileText className="h-4 w-4" />;
      case 'partner_invoice': return <FileText className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getFileTypeLabel = (type) => {
    switch (type) {
      case 'company_financial': return 'Company Financial';
      case 'expense_receipt': return 'Expense Receipt';
      case 'petrol_receipt': return 'Petrol Receipt';
      case 'ride_voucher': return 'Ride Voucher';
      case 'partner_invoice': return 'Partner Invoice/Receipt';
      default: return type;
    }
  };

  const getFileTypeBgColor = (type) => {
    switch (type) {
      case 'company_financial': return 'bg-sky-100 text-sky-700';
      case 'expense_receipt': return 'bg-red-100 text-red-700';
      case 'petrol_receipt': return 'bg-amber-100 text-amber-700';
      case 'ride_voucher': return 'bg-purple-100 text-purple-700';
      case 'partner_invoice': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getEntryTypeLabel = (type) => {
    switch (type) {
      case 'income': return 'Income';
      case 'expense': return 'Expense';
      case 'cash_income': return 'Cash Income';
      case 'cash_outcome': return 'Cash Outcome';
      case 'savings': return 'Savings';
      default: return type;
    }
  };

  const getCategoryOptions = () => {
    switch (entryType) {
      case 'income': return categories.income_categories || [];
      case 'expense': return categories.expense_categories || [];
      case 'cash_income': return categories.cash_income_categories || [];
      case 'cash_outcome': return categories.cash_outcome_categories || [];
      default: return [];
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  // Render a table for entries
  const renderEntryTable = (entryList, type, colorScheme) => {
    const bgColor = colorScheme === 'green' ? 'bg-emerald-50 border-emerald-200' : 
                    colorScheme === 'red' ? 'bg-red-50 border-red-200' :
                    colorScheme === 'amber' ? 'bg-amber-50 border-amber-200' :
                    'bg-orange-50 border-orange-200';
    const textColor = colorScheme === 'green' ? 'text-emerald-800' : 
                      colorScheme === 'red' ? 'text-red-800' :
                      colorScheme === 'amber' ? 'text-amber-800' :
                      'text-orange-800';
    const amountColor = colorScheme === 'green' ? 'text-emerald-600' : 
                        colorScheme === 'red' ? 'text-red-600' :
                        colorScheme === 'amber' ? 'text-amber-600' :
                        'text-orange-600';
    const btnColor = colorScheme === 'green' ? 'bg-emerald-600 hover:bg-emerald-700' : 
                     colorScheme === 'red' ? 'bg-red-600 hover:bg-red-700' :
                     colorScheme === 'amber' ? 'bg-amber-600 hover:bg-amber-700' :
                     'bg-orange-600 hover:bg-orange-700';
    const Icon = colorScheme === 'green' || colorScheme === 'amber' ? TrendingUp : TrendingDown;
    const label = getEntryTypeLabel(type);
    const showVehicle = type === 'expense' || type === 'cash_outcome';
    const isReadOnly = type === 'savings'; // savings are auto-managed (mirror of taxable income)

    return (
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className={`px-6 py-4 ${bgColor} border-b flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${amountColor}`} />
            <h3 className={`font-semibold ${textColor}`}>{label} ({entryList.length})</h3>
          </div>
          {!isReadOnly && (
          <Button
            size="sm"
            onClick={() => openCreateDialog(type)}
            className={btnColor}
            data-testid={`add-${type}-btn`}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add {label}
          </Button>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full" data-testid={`${type}-table`}>
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                {showVehicle && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Vehicle</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Category</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entryList.length === 0 ? (
                <tr>
                  <td colSpan={showVehicle ? 7 : 6} className="px-4 py-8 text-center text-slate-500">
                    No {label.toLowerCase()} entries for this month
                  </td>
                </tr>
              ) : (
                entryList.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50" data-testid={`${type}-row-${entry.id}`}>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(entry.date)}</td>
                    <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        {entry.description}
                        {entry.file_name && <FileText className="h-4 w-4 text-slate-400" />}
                        {entry.ride_id && <Link className="h-4 w-4 text-blue-400" title="Linked to ride" />}
                        {entry.driver_name && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium"
                            title={`Driver: ${entry.driver_name}`}
                            data-testid={`entry-driver-name-${entry.id}`}
                          >
                            {entry.driver_name}
                          </span>
                        )}
                      </div>
                    </td>
                    {showVehicle && (
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {entry.vehicle_plate || '-'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <span className={`px-2 py-1 ${bgColor} ${textColor} rounded-full text-xs`}>
                        {entry.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => togglePaymentStatus(entry)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          entry.payment_status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        }`}
                        title={entry.payment_status === 'pending' ? 'Click to mark as completed' : 'Click to mark as pending'}
                        data-testid={`toggle-status-${entry.id}`}
                      >
                        {entry.payment_status === 'pending' 
                          ? <><Clock className="h-3 w-3" /> Pending</>
                          : <><Check className="h-3 w-3" /> Completed</>
                        }
                      </button>
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${entry.payment_status === 'pending' ? 'text-slate-400 line-through' : amountColor} text-right`}>
                      <div className="flex flex-col items-end leading-tight">
                        <span>{formatCurrency(entry.amount)}</span>
                        {entry.taxable && entry.entry_type === 'income' && (
                          <span className="text-[10px] font-normal text-amber-700" title={`Taxable at ${Math.round((taxRate || 0.24) * 100)}%`}>
                            Net: {formatCurrency(entry.amount * (1 - (taxRate || 0.24)))}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openPreviewDialog(entry)}
                          className="p-1.5 text-sky-600 hover:bg-sky-50 rounded-sm"
                          title="Preview"
                          data-testid={`preview-${type}-${entry.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {entry.file_data && (
                          <button
                            onClick={() => downloadFile(entry.file_data, entry.file_name)}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-sm"
                            title="Download file"
                            data-testid={`download-${type}-${entry.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        {!entry.ride_id && !isReadOnly && (
                          <button
                            onClick={() => openEditDialog(entry)}
                            className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-sm"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {!isReadOnly && (
                        <button
                          onClick={() => { setEntryToDelete(entry); setDeleteDialogOpen(true); }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"
                          title={entry.ride_id ? "Delete auto-created entry" : "Delete"}
                          data-testid={`delete-${type}-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                        {isReadOnly && entry.linked_income_id && (
                          <span className="text-[10px] text-slate-400 italic px-1.5" title="Managed by linked POS income">
                            Auto
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Company Financials</h1>
          <p className="text-slate-600 mt-1">Internal company accounting</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const cur = Math.round((taxRate || 0.24) * 100);
              const input = window.prompt('Set POS income tax rate (percent, e.g. 24 for 24%):', String(cur));
              if (input === null) return;
              const pct = parseFloat(input);
              if (isNaN(pct) || pct < 0 || pct > 100) {
                toast.error('Enter a value between 0 and 100');
                return;
              }
              const value = pct / 100;
              axios.put(`${API}/settings/income-tax-rate`, { value }, {
                headers: { Authorization: `Bearer ${token}` }
              }).then(() => {
                setTaxRate(value);
                toast.success(`Income tax rate set to ${pct}%`);
                fetchData();
              }).catch(() => toast.error('Failed to update tax rate'));
            }}
            className="flex items-center gap-2"
            title="Configure POS income tax rate"
            data-testid="config-tax-rate-btn"
          >
            <span className="text-xs">Tax: {Math.round((taxRate || 0.24) * 100)}%</span>
          </Button>
          <Button
            onClick={() => { setTransferData({ direction: 'cash_to_card', amount: '', date: new Date().toISOString().split('T')[0], description: '', notes: '' }); setTransferOpen(true); }}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
            data-testid="transfer-btn"
          >
            <ArrowLeftRight className="h-4 w-4" />
            Transfer
          </Button>
          <Button
            variant="outline"
            onClick={openAllFilesDialog}
            className="flex items-center gap-2"
            data-testid="all-files-btn"
          >
            <FolderArchive className="h-4 w-4" />
            All Files
          </Button>
        </div>
      </div>

      {/* Month/Year Selector */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 mb-6">
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth('prev')}
            data-testid="prev-month-btn"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center gap-3">
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px]" data-testid="month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]" data-testid="year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth('next')}
            data-testid="next-month-btn"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Search Input */}
        <div className="flex items-center gap-2 mt-4 max-w-md mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by description, category, vehicle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="financial-search-input"
            />
          </div>
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchQuery('')}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Advanced Filters */}
        <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Advanced Filters</span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAdvancedFilters}
                className="text-slate-500 hover:text-slate-700 ml-auto"
              >
                Clear Filters
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Category Filter */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Category</label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full" data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {allCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date From */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Date From</label>
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full"
                data-testid="filter-date-from"
              />
            </div>
            
            {/* Date To */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Date To</label>
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full"
                data-testid="filter-date-to"
              />
            </div>
            
            {/* Amount Min */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Min Amount (€)</label>
              <Input
                type="number"
                placeholder="0"
                value={filterAmountMin}
                onChange={(e) => setFilterAmountMin(e.target.value)}
                className="w-full"
                min="0"
                step="0.01"
                data-testid="filter-amount-min"
              />
            </div>
            
            {/* Amount Max */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Max Amount (€)</label>
              <Input
                type="number"
                placeholder="No limit"
                value={filterAmountMax}
                onChange={(e) => setFilterAmountMax(e.target.value)}
                className="w-full"
                min="0"
                step="0.01"
                data-testid="filter-amount-max"
              />
            </div>
            
            {/* Payment Status */}
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="filter-status"
              >
                <option value="all">All</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
        {/* Previous Balance */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-sm ${summary.previous_balance >= 0 ? 'bg-slate-100' : 'bg-orange-100'}`}>
              <DollarSign className={`h-4 w-4 ${summary.previous_balance >= 0 ? 'text-slate-600' : 'text-orange-600'}`} />
            </div>
            <span className="text-xs font-medium text-slate-600">Previous Balance</span>
          </div>
          <p className={`text-xl font-bold ${summary.previous_balance >= 0 ? 'text-slate-700' : 'text-orange-600'}`} data-testid="previous-balance">
            {formatCurrency(summary.previous_balance)}
          </p>
        </div>

        {/* Card Income (Bank/POS) */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-emerald-100 rounded-sm">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
            </div>
            <span className="text-xs font-medium text-slate-600">Card Income</span>
          </div>
          <p className="text-xl font-bold text-emerald-600" data-testid="total-income">
            {formatCurrency(summary.total_income)}
          </p>
          {(summary.total_income_taxable_gross || 0) > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
              <p className="text-[11px] text-slate-500 flex items-center justify-between">
                <span>− Tax ({Math.round((summary.tax_rate || taxRate || 0.24) * 100)}%)</span>
                <span className="text-rose-600">−{formatCurrency(summary.total_income_tax)}</span>
              </p>
              <p className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                <span>Net</span>
                <span className="text-emerald-700" data-testid="total-income-net">{formatCurrency(summary.total_income_net)}</span>
              </p>
            </div>
          )}
        </div>

        {/* Card Outcome */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-amber-100 rounded-sm">
              <TrendingDown className="h-4 w-4 text-amber-600" />
            </div>
            <span className="text-xs font-medium text-slate-600">Card Outcome</span>
          </div>
          <p className="text-xl font-bold text-amber-600" data-testid="total-expenses">
            {formatCurrency(summary.total_expenses)}
          </p>
        </div>

        {/* Card Balance */}
        <div className="bg-white border border-emerald-200 rounded-sm shadow-sm p-4 bg-emerald-50">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-sm ${(summary.bank_balance || 0) >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <CreditCard className={`h-4 w-4 ${(summary.bank_balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
            <span className="text-xs font-medium text-slate-600">Card Balance</span>
          </div>
          <p className={`text-xl font-bold ${(summary.bank_balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="card-balance">
            {formatCurrency(summary.bank_balance || 0)}
          </p>
          {summary.previous_bank_balance !== 0 && (
            <p className="text-xs text-slate-500 mt-1">
              Previous: {formatCurrency(summary.previous_bank_balance || 0)}
            </p>
          )}
        </div>

        {/* Cash Income */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-emerald-100 rounded-sm">
              <Banknote className="h-4 w-4 text-emerald-600" />
            </div>
            <span className="text-xs font-medium text-slate-600">Cash Income</span>
          </div>
          <p className="text-xl font-bold text-emerald-600" data-testid="cash-income">
            {formatCurrency(summary.total_cash_income)}
          </p>
        </div>

        {/* Cash Outcome */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-amber-100 rounded-sm">
              <Banknote className="h-4 w-4 text-amber-600" />
            </div>
            <span className="text-xs font-medium text-slate-600">Cash Outcome</span>
          </div>
          <p className="text-xl font-bold text-amber-600" data-testid="cash-outcome">
            {formatCurrency(summary.total_cash_outcome)}
          </p>
        </div>

        {/* Cash Balance */}
        <div className="bg-white border border-emerald-200 rounded-sm shadow-sm p-4 bg-emerald-50">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-sm ${(summary.cash_balance || 0) >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <Banknote className={`h-4 w-4 ${(summary.cash_balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
            <span className="text-xs font-medium text-slate-600">Cash Balance</span>
          </div>
          <p className={`text-xl font-bold ${(summary.cash_balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="cash-balance">
            {formatCurrency(summary.cash_balance || 0)}
          </p>
          {summary.previous_cash_balance !== 0 && (
            <p className="text-xs text-slate-500 mt-1">
              Previous: {formatCurrency(summary.previous_cash_balance || 0)}
            </p>
          )}
        </div>

        {/* Total Balance */}
        <div className="bg-white border border-sky-200 rounded-sm shadow-sm p-4 bg-sky-50">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-sm ${summary.total_balance >= 0 ? 'bg-sky-100' : 'bg-orange-100'}`}>
              <DollarSign className={`h-4 w-4 ${summary.total_balance >= 0 ? 'text-sky-600' : 'text-orange-600'}`} />
            </div>
            <span className="text-xs font-medium text-slate-600">Total Balance</span>
          </div>
          <p className={`text-xl font-bold ${summary.total_balance >= 0 ? 'text-sky-600' : 'text-orange-600'}`} data-testid="total-balance">
            {formatCurrency(summary.total_balance)}
          </p>
        </div>

        {/* Savings (24% tax tracked) */}
        <div className="bg-white border border-amber-200 rounded-sm shadow-sm p-4 bg-amber-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-sm bg-amber-100">
              <PiggyBank className="h-4 w-4 text-amber-700" />
            </div>
            <span className="text-xs font-medium text-slate-600">Savings ({Math.round((summary.tax_rate || taxRate || 0.24) * 100)}%)</span>
          </div>
          <p className="text-xl font-bold text-amber-700" data-testid="total-savings">
            {formatCurrency(summary.total_savings || 0)}
          </p>
          {summary.previous_savings ? (
            <p className="text-[11px] text-slate-500 mt-0.5">
              Previous: {formatCurrency(summary.previous_savings || 0)}
            </p>
          ) : null}
          {(summary.total_savings_count || 0) > 0 && (
            <p className="text-[11px] text-slate-500 mt-0.5">{summary.total_savings_count} entries (this month)</p>
          )}
        </div>

        {/* Card + Savings combined (gross before tax deduction) */}
        <div className="bg-white border border-indigo-200 rounded-sm shadow-sm p-4 bg-indigo-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-sm bg-indigo-100">
              <CreditCard className="h-4 w-4 text-indigo-700" />
            </div>
            <span className="text-xs font-medium text-slate-600">Card + Savings</span>
          </div>
          <p className="text-xl font-bold text-indigo-700" data-testid="card-plus-savings">
            {formatCurrency((summary.bank_balance || 0) + (summary.total_savings || 0))}
          </p>
          {((summary.previous_bank_balance || 0) + (summary.previous_savings || 0)) !== 0 && (
            <p className="text-[11px] text-slate-500 mt-0.5">
              Previous: {formatCurrency((summary.previous_bank_balance || 0) + (summary.previous_savings || 0))}
            </p>
          )}
          <p className="text-[11px] text-slate-500 mt-0.5">Gross (before tax deduction)</p>
        </div>
      </div>

      {/* Tabs for Card, Cash and Savings */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="card" data-testid="card-tab">
            <CreditCard className="h-4 w-4 mr-2" />
            Card
          </TabsTrigger>
          <TabsTrigger value="cash" data-testid="cash-tab">
            <Banknote className="h-4 w-4 mr-2" />
            Cash
          </TabsTrigger>
          <TabsTrigger value="savings" data-testid="savings-tab">
            <PiggyBank className="h-4 w-4 mr-2" />
            Savings
          </TabsTrigger>
        </TabsList>

        {/* Card Tab (Bank/POS Payments) */}
        <TabsContent value="card" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderEntryTable(incomeEntries, 'income', 'green')}
            {renderEntryTable(expenseEntries, 'expense', 'red')}
          </div>
        </TabsContent>

        {/* Cash Tab */}
        <TabsContent value="cash" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderEntryTable(cashIncomeEntries, 'cash_income', 'amber')}
            {renderEntryTable(cashOutcomeEntries, 'cash_outcome', 'orange')}
          </div>
        </TabsContent>

        {/* Savings Tab (auto-tracked deductions on taxable POS income) */}
        <TabsContent value="savings" className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 flex items-start gap-2">
            <PiggyBank className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Auto-tracked tax deductions ({Math.round((taxRate || 0.24) * 100)}%)</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Each entry below is automatically generated from a taxable POS income and mirrors its details.
                To edit/delete, modify the linked Card income.
              </p>
            </div>
          </div>
          {renderEntryTable(savingsEntries, 'savings', 'amber')}
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewEntry?.entry_type === 'income' || previewEntry?.entry_type === 'cash_income' ? (
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-600" />
              )}
              {getEntryTypeLabel(previewEntry?.entry_type)} Details
            </DialogTitle>
          </DialogHeader>
          
          {previewEntry && (
            <div className="space-y-6">
              {/* Entry Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Date</Label>
                  <p className="font-medium text-slate-900">{formatDate(previewEntry.date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Amount</Label>
                  <p className={`font-bold text-xl ${
                    previewEntry.entry_type === 'income' || previewEntry.entry_type === 'cash_income' 
                      ? 'text-emerald-600' 
                      : 'text-red-600'
                  }`}>
                    {formatCurrency(previewEntry.amount)}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Description</Label>
                <p className="font-medium text-slate-900">{previewEntry.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Category</Label>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                    previewEntry.entry_type === 'income' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : previewEntry.entry_type === 'expense'
                      ? 'bg-red-100 text-red-700'
                      : previewEntry.entry_type === 'cash_income'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {previewEntry.category}
                  </span>
                </div>
                {previewEntry.vehicle_plate && (
                  <div>
                    <Label className="text-xs text-slate-500">Vehicle</Label>
                    <p className="font-medium text-slate-900">{previewEntry.vehicle_plate}</p>
                  </div>
                )}
              </div>

              {previewEntry.driver_name && (
                <div>
                  <Label className="text-xs text-slate-500">Driver</Label>
                  <p className="font-medium text-slate-900" data-testid="preview-driver-name">
                    {previewEntry.driver_name}
                  </p>
                </div>
              )}

              {previewEntry.ride_id && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <div className="flex items-center gap-2 text-blue-700">
                    <Link className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Auto-created from {previewEntry.entry_type === 'income' ? 'POS' : 'Cash'} Ride
                      {previewEntry.driver_name ? ` · Driver: ${previewEntry.driver_name}` : ''}
                    </span>
                  </div>
                </div>
              )}

              {previewEntry.notes && (
                <div>
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-sm">{previewEntry.notes}</p>
                </div>
              )}

              {/* Attached File Section */}
              <div className="border-t border-slate-200 pt-4">
                <Label className="text-xs text-slate-500 mb-2 block">Attached File</Label>
                {previewEntry.file_data ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-slate-50 p-3 rounded-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700">{previewEntry.file_name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadFile(previewEntry.file_data, previewEntry.file_name)}
                        className="flex items-center gap-1"
                        data-testid="download-preview-file"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </Button>
                    </div>
                    
                    {/* File Preview */}
                    {isImageFile(previewEntry.file_name) && (
                      <div className="border border-slate-200 rounded-sm p-2">
                        <img 
                          src={previewEntry.file_data} 
                          alt={previewEntry.file_name}
                          className="max-w-full max-h-[300px] mx-auto object-contain"
                        />
                      </div>
                    )}
                    {isPdfFile(previewEntry.file_name) && (
                      <div className="border border-slate-200 rounded-sm p-4 bg-slate-50 text-center">
                        <FileText className="h-12 w-12 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">PDF Document</p>
                        <p className="text-xs text-slate-500">Click Download to view</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No file attached</p>
                )}
              </div>

              {/* Metadata */}
              <div className="border-t border-slate-200 pt-4 text-xs text-slate-500">
                <div className="flex justify-between">
                  <span>Created: {formatDateTime(previewEntry.created_at)}</span>
                  {previewEntry.updated_at !== previewEntry.created_at && (
                    <span>Updated: {formatDateTime(previewEntry.updated_at)}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* All Files Dialog */}
      <Dialog open={allFilesOpen} onOpenChange={setAllFilesOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderArchive className="h-5 w-5 text-slate-600" />
              All Uploaded Files
            </DialogTitle>
            <DialogDescription>
              View and download all files uploaded across the application
            </DialogDescription>
          </DialogHeader>
          
          {loadingFiles ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            </div>
          ) : allFiles.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FolderArchive className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No files uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allFiles.map((file, index) => (
                <div 
                  key={`${file.type}-${file.id || index}`}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-sm hover:bg-slate-100 transition-colors"
                  data-testid={`file-item-${index}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-sm ${getFileTypeBgColor(file.type)}`}>
                      {getFileTypeIcon(file.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{file.file_name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className={`px-1.5 py-0.5 rounded ${getFileTypeBgColor(file.type)}`}>
                          {getFileTypeLabel(file.type)}
                        </span>
                        {file.description && <span>• {file.description}</span>}
                        {file.date && <span>• {formatDate(file.date)}</span>}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (file.file_data) {
                        downloadFile(file.file_data, file.file_name);
                      } else if (file.url) {
                        downloadFileFromUrl(file.url, file.file_name);
                      }
                    }}
                    className="flex items-center gap-1 ml-2"
                    data-testid={`download-file-${index}`}
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          <div className="border-t border-slate-200 pt-4 mt-4">
            <p className="text-xs text-slate-500 text-center">
              Total: {allFiles.length} file{allFiles.length !== 1 ? 's' : ''}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Edit' : 'Add'} {getEntryTypeLabel(entryType)}
            </DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update the entry details below.' : 'Enter the details for the new entry.'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="entry-date"
                />
              </div>
              <div>
                <Label htmlFor="amount">Amount (€) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="entry-amount"
                />
              </div>
            </div>
            
            {/* Taxable toggle - only for POS Income (entry_type='income') */}
            {entryType === 'income' && (
              <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="flex-1">
                  <Label htmlFor="taxable-toggle" className="text-sm font-medium text-amber-900 cursor-pointer">
                    Subject to {Math.round((taxRate || 0.24) * 100)}% income tax
                  </Label>
                  <p className="text-xs text-amber-700 mt-0.5">
                    When enabled, the bank balance uses the net amount (gross − tax).
                    {formData.amount && !isNaN(parseFloat(formData.amount)) && formData.taxable && (
                      <> Net: <strong>€{(parseFloat(formData.amount) * (1 - (taxRate || 0.24))).toFixed(2)}</strong></>
                    )}
                  </p>
                </div>
                <Switch
                  id="taxable-toggle"
                  checked={!!formData.taxable}
                  onCheckedChange={(v) => setFormData({ ...formData, taxable: v })}
                  data-testid="entry-taxable-toggle"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the entry"
                required
                data-testid="entry-description"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category *</Label>
              <CategorySelect
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
                categories={getCategoryOptions()}
                categoryType={entryType}
                placeholder="Select a category"
                token={token}
                onCategoryAdded={fetchCategories}
                testId="entry-category"
              />
            </div>
            
            {(entryType === 'expense' || entryType === 'cash_outcome') && (
              <div>
                <Label htmlFor="vehicle">Vehicle (optional)</Label>
                <Select 
                  value={formData.vehicle_id} 
                  onValueChange={(v) => setFormData({ ...formData, vehicle_id: v })}
                >
                  <SelectTrigger data-testid="entry-vehicle">
                    <SelectValue placeholder="Select a vehicle (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.plate_number} {vehicle.model && `- ${vehicle.model}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or details"
                rows={2}
                data-testid="entry-notes"
              />
            </div>
            
            <div>
              <Label>Payment Status</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_status: 'completed' })}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium border transition-colors ${
                    formData.payment_status === 'completed'
                      ? 'bg-emerald-100 border-emerald-400 text-emerald-800'
                      : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                  data-testid="status-completed-btn"
                >
                  Completed
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payment_status: 'pending' })}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium border transition-colors ${
                    formData.payment_status === 'pending'
                      ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                      : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                  data-testid="status-pending-btn"
                >
                  Pending
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Pending entries are tracked but not counted in totals.
              </p>
            </div>
            
            <div>
              <Label>File/Receipt (optional)</Label>
              {formData.file_data ? (
                <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <FileText className="h-5 w-5 text-slate-500" />
                  <span className="flex-1 text-sm text-slate-700 truncate">{formData.file_name}</span>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="p-1 text-slate-500 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-1">
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-slate-400 transition-colors">
                    <Upload className="h-5 w-5 text-slate-400" />
                    <span className="text-sm text-slate-500">Click to upload file</span>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="hidden"
                      data-testid="file-upload"
                    />
                  </label>
                  <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG, DOC up to 10MB</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className={
                  entryType === 'income' ? 'bg-emerald-600 hover:bg-emerald-700' : 
                  entryType === 'expense' ? 'bg-red-600 hover:bg-red-700' :
                  entryType === 'cash_income' ? 'bg-amber-600 hover:bg-amber-700' :
                  'bg-orange-600 hover:bg-orange-700'
                }
                data-testid="save-entry-btn"
              >
                {editingEntry ? 'Update' : 'Create'} Entry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {getEntryTypeLabel(entryToDelete?.entry_type)?.toLowerCase()} entry? 
              This action cannot be undone.
              {entryToDelete?.ride_id && (
                <span className="mt-2 block rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                  ⚠ Questa entrata è stata creata automaticamente dalla corsa e non verrà ricreata: eliminandola dovrai gestirla manualmente se necessario.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-indigo-600" />
              Transfer Funds
            </DialogTitle>
            <DialogDescription>Move funds between Cash and Card accounts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Direction</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { val: 'cash_to_card',     label: 'Cash → Card',    icons: [Banknote, CreditCard] },
                  { val: 'card_to_cash',     label: 'Card → Cash',    icons: [CreditCard, Banknote] },
                  { val: 'cash_to_savings',  label: 'Cash → Savings', icons: [Banknote, PiggyBank] },
                  { val: 'savings_to_cash',  label: 'Savings → Cash', icons: [PiggyBank, Banknote] },
                  { val: 'card_to_savings',  label: 'Card → Savings', icons: [CreditCard, PiggyBank] },
                  { val: 'savings_to_card',  label: 'Savings → Card', icons: [PiggyBank, CreditCard] },
                ].map((d) => {
                  const [IconFrom, IconTo] = d.icons;
                  const active = transferData.direction === d.val;
                  return (
                    <Button
                      key={d.val}
                      type="button"
                      variant={active ? 'default' : 'outline'}
                      className={`${active ? 'bg-indigo-600 hover:bg-indigo-700' : ''} h-auto py-2`}
                      onClick={() => setTransferData(prev => ({ ...prev, direction: d.val }))}
                      data-testid={`transfer-${d.val.replace(/_/g,'-')}`}
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="flex items-center gap-1">
                          <IconFrom className="h-3.5 w-3.5" />
                          <ArrowLeftRight className="h-3 w-3" />
                          <IconTo className="h-3.5 w-3.5" />
                        </div>
                        <span className="text-[10px] font-medium">{d.label}</span>
                      </div>
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">
                {(() => {
                  const labels = {
                    cash_to_card:    'Cash will decrease, Card will increase',
                    card_to_cash:    'Card will decrease, Cash will increase',
                    cash_to_savings: 'Cash will decrease, Savings will increase',
                    savings_to_cash: 'Savings will decrease, Cash will increase',
                    card_to_savings: 'Card will decrease, Savings will increase',
                    savings_to_card: 'Savings will decrease, Card will increase',
                  };
                  return labels[transferData.direction] || '';
                })()}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={transferData.amount}
                  onChange={(e) => setTransferData(prev => ({ ...prev, amount: e.target.value }))}
                  data-testid="transfer-amount"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={transferData.date}
                  onChange={(e) => setTransferData(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="transfer-date"
                />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="e.g. Cash deposit to bank"
                value={transferData.description}
                onChange={(e) => setTransferData(prev => ({ ...prev, description: e.target.value }))}
                data-testid="transfer-description"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Additional details..."
                value={transferData.notes}
                onChange={(e) => setTransferData(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="transfer-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button
              onClick={handleTransfer}
              disabled={submittingTransfer}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-transfer-btn"
            >
              {submittingTransfer ? 'Processing...' : 'Confirm Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
